import { useEffect, useRef, useState, useCallback } from "react";
import Picker from "emoji-picker-react";
import { comment, searchMentions } from "../../functions/post";
import { uploadImages } from "../../functions/uploadImages";
import dataURItoBlob from "../../helpers/dataURItoBlob";
import { ClipLoader } from "react-spinners";

export default function CreateComment({
  user,
  postId,
  setComments,
  setCount,
  replyTo,
  commentId,
  onReply,
  onCancel,
  placeholder = "Write a comment...",
}) {
  const [picker, setPicker] = useState(false);
  const [showGifPicker, setShowGifPicker] = useState(false);
  const [text, setText] = useState(replyTo ? `@${replyTo} ` : "");
  const [error, setError] = useState("");
  const [commentImage, setCommentImage] = useState("");
  const [selectedGif, setSelectedGif] = useState("");
  const [cursorPosition, setCursorPosition] = useState();
  const [loading, setLoading] = useState(false);
  const [gifs, setGifs] = useState([]);
  const [gifSearch, setGifSearch] = useState("");
  const [showMentions, setShowMentions] = useState(false);
  const [mentionSearch, setMentionSearch] = useState("");
  const [mentionResults, setMentionResults] = useState([]);
  const [mentions, setMentions] = useState([]);
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState("");

  const textRef = useRef(null);
  const imgInput = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  // Giphy API key (use sua própria key)
  const GIPHY_API_KEY = "GlVGYHkr3WSBnllca54iNt0yFbjz7L65";

  useEffect(() => {
    if (textRef.current) {
      textRef.current.selectionEnd = cursorPosition;
    }
  }, [cursorPosition]);

  // Buscar GIFs
  const fetchGifs = useCallback(async (search = "") => {
    try {
      const endpoint = search
        ? `https://api.giphy.com/v1/gifs/search?api_key=${GIPHY_API_KEY}&q=${search}&limit=20`
        : `https://api.giphy.com/v1/gifs/trending?api_key=${GIPHY_API_KEY}&limit=20`;
      const response = await fetch(endpoint);
      const data = await response.json();
      setGifs(data.data || []);
    } catch (error) {
      console.error("Error fetching GIFs:", error);
    }
  }, []);

  useEffect(() => {
    if (showGifPicker) {
      fetchGifs(gifSearch);
    }
  }, [showGifPicker, gifSearch, fetchGifs]);

  // Buscar menções
  useEffect(() => {
    const searchUsers = async () => {
      if (mentionSearch.length > 0) {
        const results = await searchMentions(mentionSearch, user.token);
        setMentionResults(results);
      } else {
        setMentionResults([]);
      }
    };
    searchUsers();
  }, [mentionSearch, user.token]);

  const handleEmoji = (e, emojiObject) => {
    const ref = textRef.current;
    ref.focus();
    const start = text.substring(0, ref.selectionStart);
    const end = text.substring(ref.selectionStart);
    const newText = start + emojiObject.emoji + end;
    setText(newText);
    setCursorPosition(start.length + emojiObject.emoji.length);
  };

  const handleImage = (e) => {
    let file = e.target.files[0];
    if (!file) return;

    if (
      file.type !== "image/jpeg" &&
      file.type !== "image/png" &&
      file.type !== "image/webp" &&
      file.type !== "image/gif"
    ) {
      setError(`${file.name} format is not supported.`);
      return;
    }

    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      setCommentImage(event.target.result);
      setSelectedGif("");
    };
  };

  const handleTextChange = (e) => {
    const value = e.target.value;
    setText(value);

    // Detectar menção
    const lastWord = value.split(/\s/).pop();
    if (lastWord.startsWith("@") && lastWord.length > 1) {
      setMentionSearch(lastWord.slice(1));
      setShowMentions(true);
    } else {
      setShowMentions(false);
      setMentionSearch("");
    }
  };

  const handleMentionSelect = (friend) => {
    const words = text.split(/\s/);
    words.pop();
    const newText = words.join(" ") + (words.length ? " " : "") + `@${friend.username} `;
    setText(newText);
    setMentions([...mentions, friend._id]);
    setShowMentions(false);
    setMentionSearch("");
    textRef.current.focus();
  };

  const handleGifSelect = (gif) => {
    setSelectedGif(gif.images.fixed_height.url);
    setCommentImage("");
    setShowGifPicker(false);
  };

  // Gravação de áudio
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };

      mediaRecorderRef.current.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: "audio/webm" });
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach((track) => track.stop());
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
    } catch (error) {
      setError("Could not access microphone");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const clearAudio = () => {
    setAudioBlob(null);
    setAudioUrl("");
  };

  const handleComment = async (e) => {
    const hasContent = text.trim() !== "" || commentImage || selectedGif || audioBlob;
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (!loading && hasContent) {
        await submitComment();
      }
    }
  };

  const submitComment = async () => {
    if (text.trim() === "" && !commentImage && !selectedGif && !audioBlob) return;

    setLoading(true);
    try {
      let imgCommentUrl = "";
      let audioCommentUrl = "";

      if (commentImage) {
        const img = dataURItoBlob(commentImage);
        const path = `${user.username}/post_images/${postId}`;
        let formData = new FormData();
        formData.append("path", path);
        formData.append("file", img);
        const imgResponse = await uploadImages(formData, path, user.token);
        imgCommentUrl = imgResponse[0].url;
      }

      if (audioBlob) {
        const path = `${user.username}/audio/${postId}`;
        let formData = new FormData();
        formData.append("path", path);
        formData.append("file", audioBlob, "audio.webm");
        const audioResponse = await uploadImages(formData, path, user.token);
        audioCommentUrl = audioResponse[0].url;
      }

      const commentData = {
        text: text.trim(),
        image: imgCommentUrl,
        gif: selectedGif,
        audio: audioCommentUrl,
        mentions: mentions,
      };

      if (onReply && commentId) {
        // É uma resposta
        await onReply(commentData);
      } else {
        // É um novo comentário
        const newComments = await comment(postId, commentData, user.token);
        if (Array.isArray(newComments)) {
          setComments(newComments);
          setCount((prev) => prev + 1);
          // Limpar somente em sucesso
          setText("");
          setCommentImage("");
          setSelectedGif("");
          setAudioBlob(null);
          setAudioUrl("");
          setMentions([]);
        } else {
          // Erro vindo da API (string)
          setError(newComments || "Erro ao comentar");
        }
      }
    } catch (err) {
      setError("An error occurred while posting your comment.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="create_comment_wrap">
      <div className="create_comment">
        <img src={user?.picture} alt="User avatar" />
        <div className="comment_input_wrap">
          {picker && (
            <div className="comment_emoji_picker">
              <Picker onEmojiClick={handleEmoji} />
            </div>
          )}

          {showGifPicker && (
            <div className="gif_picker">
              <div className="gif_picker_header">
                <input
                  type="text"
                  placeholder="Search GIFs..."
                  value={gifSearch}
                  onChange={(e) => setGifSearch(e.target.value)}
                />
                <div
                  className="gif_picker_close"
                  onClick={() => setShowGifPicker(false)}
                >
                  <i className="exit_icon"></i>
                </div>
              </div>
              <div className="gif_picker_grid">
                {gifs.map((gif) => (
                  <img
                    key={gif.id}
                    src={gif.images.fixed_height_small.url}
                    alt={gif.title}
                    onClick={() => handleGifSelect(gif)}
                  />
                ))}
              </div>
            </div>
          )}

          {showMentions && mentionResults.length > 0 && (
            <div className="mention_dropdown">
              {mentionResults.map((friend) => (
                <div
                  key={friend._id}
                  className="mention_item"
                  onClick={() => handleMentionSelect(friend)}
                >
                  <img src={friend.picture} alt="" />
                  <span>
                    {friend.first_name} {friend.last_name}
                  </span>
                </div>
              ))}
            </div>
          )}

          <input
            type="file"
            hidden
            ref={imgInput}
            accept="image/jpeg,image/png,image/gif,image/webp"
            onChange={handleImage}
          />

          {error && (
            <div className="postError comment_error">
              <div className="postError_error">{error}</div>
              <button className="blue_btn" onClick={() => setError("")}>
                Try again
              </button>
            </div>
          )}

          <textarea
            ref={textRef}
            value={text}
            placeholder={placeholder}
            onChange={handleTextChange}
            onKeyDown={handleComment}
            rows={1}
          />

          <div className="comment_actions_row">
            {loading && (
              <div className="comment_circle" style={{ marginTop: "5px" }}>
                <ClipLoader size={20} color="#1876f2" loading={loading} />
              </div>
            )}
            <div
              className="comment_circle_icon hover2"
              onClick={() => {
                setPicker((prev) => !prev);
                setShowGifPicker(false);
              }}
            >
              <i className="emoji_icon"></i>
            </div>
            <div
              className="comment_circle_icon hover2"
              onClick={() => imgInput.current.click()}
            >
              <i className="camera_icon"></i>
            </div>
            <div
              className="comment_circle_icon hover2"
              onClick={() => {
                setShowGifPicker((prev) => !prev);
                setPicker(false);
              }}
            >
              <i className="gif_icon"></i>
            </div>
            <div
              className={`comment_circle_icon hover2 ${isRecording ? "recording" : ""}`}
              onClick={isRecording ? stopRecording : startRecording}
            >
              <i className={isRecording ? "stop_icon" : "mic_icon"}></i>
            </div>
            {!onReply && (
              <button
                type="button"
                className="comment_send_btn"
                onClick={submitComment}
                disabled={loading || (text.trim() === "" && !commentImage && !selectedGif && !audioBlob)}
              >
                Enviar
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Preview de imagem */}
      {commentImage && (
        <div className="comment_img_preview">
          <img src={commentImage} alt="Preview" />
          <div className="small_white_circle" onClick={() => setCommentImage("")}>
            <i className="exit_icon"></i>
          </div>
        </div>
      )}

      {/* Preview de GIF */}
      {selectedGif && (
        <div className="comment_img_preview">
          <img src={selectedGif} alt="GIF Preview" />
          <div className="small_white_circle" onClick={() => setSelectedGif("")}>
            <i className="exit_icon"></i>
          </div>
        </div>
      )}

      {/* Preview de áudio */}
      {audioUrl && (
        <div className="comment_audio_preview">
          <audio controls src={audioUrl} />
          <div className="small_white_circle" onClick={clearAudio}>
            <i className="exit_icon"></i>
          </div>
        </div>
      )}

      {/* Botões de ação para resposta */}
      {onReply && (
        <div className="reply_actions">
          <button className="reply_cancel" onClick={onCancel}>
            Cancel
          </button>
          <button
            className="reply_submit"
            onClick={submitComment}
            disabled={loading}
          >
            Reply
          </button>
        </div>
      )}
    </div>
  );
}
